import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { useLanguage } from "./LanguageContext";
import { apiFetch } from '../lib/apiFetch';
// ─── Styles ──────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

  .wiz-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15,23,42,0.45);
    backdrop-filter: blur(3px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 200;
    padding: 20px;
  }

  .wiz-shell {
    background: #fff;
    border-radius: 14px;
    width: 980px;
    max-width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 80px rgba(0,0,0,0.2);
    overflow: hidden;
    font-family: 'new-science','DM Sans',sans-serif;
  }

  /* ── Header / Stepper ── */
  .wiz-header {
    padding: 20px 32px 0;
    border-bottom: 1px solid #e2e8f0;
  }

  .wiz-steps {
    display: flex;
    gap: 0;
    margin-bottom: 0;
  }

  .wiz-step {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 24px 16px 0;
    cursor: default;
    opacity: 0.4;
    transition: opacity 0.2s;
  }

  .wiz-step.done  { opacity: 0.6; }
  .wiz-step.active { opacity: 1; }

  .wiz-step-num {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 2px solid #cbd5e1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    color: #94a3b8;
    flex-shrink: 0;
    transition: all 0.2s;
  }

  .wiz-step.active .wiz-step-num {
    border-color: #2563eb;
    background: #2563eb;
    color: white;
  }

  .wiz-step.done .wiz-step-num {
    border-color: #22c55e;
    background: #22c55e;
    color: white;
  }

  .wiz-step-label {
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
    white-space: nowrap;
  }

  .wiz-step.active .wiz-step-label { color: #0f172a; font-weight: 600; }
  .wiz-step.done  .wiz-step-label  { color: #22c55e; }

  .wiz-step-divider {
    flex: 1;
    height: 1px;
    background: #e2e8f0;
    margin: 0 12px 16px;
    align-self: center;
    min-width: 20px;
  }

  /* ── Body ── */
  .wiz-body {
    display: flex;
    flex: 1;
    overflow: hidden;
    min-height: 0;
  }

  .wiz-main {
    flex: 1;
    padding: 28px 32px;
    overflow-y: auto;
  }

  .wiz-aside {
    width: 320px;
    border-left: 1px solid #e2e8f0;
    background: #f8fafc;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .wiz-aside-header {
    padding: 18px 20px 12px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .wiz-aside-title {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
  }

  .wiz-aside-badge {
    font-size: 11px;
    font-weight: 600;
    color: #2563eb;
    background: #eff6ff;
    border-radius: 100px;
    padding: 2px 8px;
  }

  .wiz-aside-list {
    flex: 1;
    overflow-y: auto;
    padding: 10px 12px;
  }

  .wiz-suggestion {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 9px 10px;
    border-radius: 7px;
    cursor: pointer;
    transition: background 0.15s;
    border: 1.5px solid transparent;
    margin-bottom: 4px;
  }

  .wiz-suggestion:hover {
    background: #f1f5f9;
  }

  .wiz-suggestion.selected {
    background: #eff6ff;
    border-color: #bfdbfe;
  }

  .wiz-suggestion-text {
    font-size: 13px;
    color: #334155;
    line-height: 1.4;
    flex: 1;
  }

  .wiz-suggestion.selected .wiz-suggestion-text {
    color: #1d4ed8;
  }

  .wiz-suggestion-check {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 1.5px solid #cbd5e1;
    flex-shrink: 0;
    margin-top: 1px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: transparent;
    transition: all 0.15s;
  }

  .wiz-suggestion.selected .wiz-suggestion-check {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }

  /* ── Footer ── */
  .wiz-footer {
    padding: 16px 32px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: white;
  }

  .wiz-footer-info {
    font-size: 12px;
    color: #94a3b8;
  }

  .wiz-footer-actions {
    display: flex;
    gap: 10px;
  }

  /* ── Buttons ── */
  .wiz-btn {
    padding: 9px 20px;
    border-radius: 7px;
    font-family: 'new-science','DM Sans',sans-serif;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    border: none;
  }

  .wiz-btn-ghost {
    background: none;
    border: 1.5px solid #e2e8f0;
    color: #64748b;
  }
  .wiz-btn-ghost:hover { border-color: #94a3b8; color: #0f172a; }

  .wiz-btn-primary {
    background: #2563eb;
    color: white;
    box-shadow: 0 2px 8px rgba(37,99,235,0.25);
  }
  .wiz-btn-primary:hover { background: #1d4ed8; }
  .wiz-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

  .wiz-btn-ai {
    background: linear-gradient(135deg, #6366f1, #2563eb);
    color: white;
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 13px;
    padding: 8px 16px;
    box-shadow: 0 2px 8px rgba(99,102,241,0.3);
  }
  .wiz-btn-ai:hover { opacity: 0.9; transform: translateY(-1px); }
  .wiz-btn-ai:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  .wiz-btn-ai svg {
    animation: none;
  }
  .wiz-btn-ai.loading svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Form elements ── */
  .wiz-field { margin-bottom: 18px; }

  .wiz-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    margin-bottom: 6px;
  }

  .wiz-input {
    width: 100%;
    padding: 10px 13px;
    border-radius: 7px;
    border: 1.5px solid #e2e8f0;
    font-family: 'new-science','DM Sans',sans-serif;
    font-size: 14px;
    color: #0f172a;
    background: #fff;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
  }
  .wiz-input:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
  }
  .wiz-input.error { border-color: #ef4444; }

  .wiz-input-row {
    display: flex;
    gap: 8px;
  }
  .wiz-input-row .wiz-input { flex: 1; }

  .wiz-url-row {
    display: flex;
    gap: 8px;
    align-items: flex-start;
  }
  .wiz-url-row .wiz-input { flex: 1; }

  /* ── URL status ── */
  .wiz-url-status {
    font-size: 12px;
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .wiz-url-status.checking { color: #64748b; }
  .wiz-url-status.ok       { color: #16a34a; }
  .wiz-url-status.fail     { color: #dc2626; }

  .wiz-url-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .checking .wiz-url-dot { background: #94a3b8; animation: pulse 1s infinite; }
  .ok       .wiz-url-dot { background: #16a34a; }
  .fail     .wiz-url-dot { background: #dc2626; }

  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }

  /* ── Tags ── */
  .wiz-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }

  .wiz-tag {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    background: #f1f5f9;
    border-radius: 100px;
    font-size: 12.5px;
    color: #334155;
    border: 1px solid #e2e8f0;
  }

  .wiz-tag-remove {
    background: none;
    border: none;
    cursor: pointer;
    color: #94a3b8;
    font-size: 13px;
    padding: 0;
    line-height: 1;
    font-family: inherit;
  }
  .wiz-tag-remove:hover { color: #ef4444; }

  /* ── Topic list (step 2) ── */
  .wiz-topic-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    margin-bottom: 6px;
    background: white;
  }

  .wiz-topic-bullet {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #2563eb;
    flex-shrink: 0;
  }

  .wiz-topic-name {
    flex: 1;
    font-size: 13.5px;
    color: #0f172a;
  }

  .wiz-topic-del {
    background: none;
    border: none;
    cursor: pointer;
    color: #cbd5e1;
    font-size: 16px;
    padding: 0;
    line-height: 1;
    transition: color 0.15s;
  }
  .wiz-topic-del:hover { color: #ef4444; }

  /* ── Search term item (step 3) ── */
  .wiz-term-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    margin-bottom: 6px;
    background: white;
  }

  .wiz-term-topic-badge {
    font-size: 11px;
    color: #2563eb;
    background: #eff6ff;
    border-radius: 4px;
    padding: 2px 7px;
    white-space: nowrap;
    margin-top: 1px;
    flex-shrink: 0;
  }

  .wiz-term-text {
    flex: 1;
    font-size: 13.5px;
    color: #0f172a;
    line-height: 1.4;
  }

  .wiz-term-del {
    background: none;
    border: none;
    cursor: pointer;
    color: #cbd5e1;
    font-size: 16px;
    padding: 0;
    line-height: 1;
    transition: color 0.15s;
    flex-shrink: 0;
  }
  .wiz-term-del:hover { color: #ef4444; }

  /* ── Step 3 topic select ── */
  .wiz-select {
    width: 100%;
    padding: 10px 13px;
    border-radius: 7px;
    border: 1.5px solid #e2e8f0;
    font-family: 'new-science','DM Sans',sans-serif;
    font-size: 14px;
    color: #0f172a;
    background: #fff;
    outline: none;
    transition: border-color 0.15s;
    box-sizing: border-box;
    margin-bottom: 10px;
    cursor: pointer;
  }
  .wiz-select:focus { border-color: #2563eb; }

  /* ── Step 4 settings ── */
  .wiz-settings-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  /* ── Error / info messages ── */
  .wiz-error {
    font-size: 12.5px;
    color: #dc2626;
    background: rgba(220,38,38,0.06);
    border: 1px solid rgba(220,38,38,0.15);
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 14px;
  }

  .wiz-info {
    font-size: 12.5px;
    color: #2563eb;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 14px;
  }

  .wiz-empty {
    text-align: center;
    padding: 32px 20px;
    color: #94a3b8;
    font-size: 13px;
  }

  .wiz-empty-icon { font-size: 28px; margin-bottom: 10px; }

  .wiz-section-title {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 4px;
  }

  .wiz-section-sub {
    font-size: 13px;
    color: #64748b;
    margin: 0 0 24px;
  }

  .wiz-spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .wiz-loading-overlay {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 40px 20px;
  }

  .wiz-loading-circle {
    width: 48px;
    height: 48px;
    border: 3.5px solid #e2e8f0;
    border-top-color: #2563eb;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .wiz-loading-text {
    margin-top: 14px;
    font-size: 13px;
    color: #94a3b8;
  }
`;

// ─── AI suggestion call via Anthropic API ────────────────────────────────────
// ─── AI suggestions via Node backend  
async function fetchAISuggestions(type, brandName, brandUrl, topics = [], appLang = "de", brandId = null, industry = null) {
  try {
    const response = await apiFetch("/suggest", {
      method: "POST",
      body: JSON.stringify({ type, brandName, brandUrl, topics, appLang, brandId, industry }),
    });
    if (!response.ok) throw new Error(`Server error: ${response.status}`);
    const data = await response.json();
    return {
      suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
      industry: data.industry || null,
    };
  } catch (err) {
    console.error("AI suggestion error:", err);
    return { suggestions: [], industry: null };
  }
}

// ─── URL helpers ─────────────────────────────────────────────────────────────
function normalizeUrl(url) {
  let u = url.trim();
  if (!u) return "";
  if (!u.startsWith("http://") && !u.startsWith("https://")) u = "https://" + u;
  return u;
}

function isValidUrlFormat(url) {
  try {
    const u = new URL(normalizeUrl(url));
    const h = u.hostname;
    // needs at least one dot + valid TLD (2+ chars) — works for www.domain.tld too
    return h.length > 0 && /\.[a-z]{2,}$/i.test(h) && !h.endsWith(".");
  } catch {
    return false;
  }
}

// DNS lookup via Cloudflare DoH — no CORS, no fetch to target domain
async function checkUrl(url) {
  const normalized = normalizeUrl(url);

  if (!isValidUrlFormat(url)) {
    return { ok: false, normalized };
  }

  const hostname = new URL(normalized).hostname;

  // Try Cloudflare DoH first, then Quad9 as fallback
  const endpoints = [
    `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(hostname)}&type=A`,
    `https://dns.quad9.net/dns-query?name=${encodeURIComponent(hostname)}&type=A`,
  ];

  for (const endpoint of endpoints) {
    try {
      const res = await fetch(endpoint, {
        headers: { Accept: "application/dns-json" },
        signal: AbortSignal.timeout(3000),
      });
      if (!res.ok) continue;
      const data = await res.json();
      // Status 0 = NOERROR + at least one A record = domain exists
      const ok = data.Status === 0 && Array.isArray(data.Answer) && data.Answer.length > 0;
      return { ok, normalized };
    } catch {
      continue; // try next endpoint
    }
  }

  // All endpoints failed (offline / all blocked) — accept the URL silently
  return { ok: true, normalized };
}

// ─── Step components ─────────────────────────────────────────────────────────

function Step1({ data, onChange, onNext, onClose, isEditMode, brandName }) {
  const { t } = useLanguage();
  const [urlStatus, setUrlStatus] = useState(null); // null | 'checking' | 'ok' | 'fail'
  const [error, setError] = useState("");
  const [checkTimer, setCheckTimer] = useState(null);

  const handleUrlChange = (val) => {
    onChange("url", val);
    setUrlStatus(null);
    if (checkTimer) clearTimeout(checkTimer);
    // Only start check when input looks like a complete domain (has a dot + min TLD)
    if (isValidUrlFormat(val)) {
      setUrlStatus("checking");
      const timer = setTimeout(async () => {
        const result = await checkUrl(val);
        setUrlStatus(result.ok ? "ok" : "fail");
        if (result.ok) onChange("url", result.normalized);
      }, 900);
      setCheckTimer(timer);
    } else {
      setUrlStatus(null); // not enough input yet — show nothing
    }
  };

  const handleNext = () => {
    if (!data.name.trim()) { setError(t("brand.errRequired")); return; }
    if (!data.url.trim())  { setError(t("brand.errRequired")); return; }
    if (urlStatus === "fail") { setError(t("wizard.urlFailErr")); return; }
    setError("");
    onNext();
  };

  return (
    <>
      <h2 className="wiz-section-title">
        {isEditMode ? `${t("wizard.editBrandTitle")}: ${brandName}` : t("brand.createTitle")}
      </h2>
      <p className="wiz-section-sub">{t("wizard.brandBasicInfo")}</p>
      <p style={{fontSize:13, color:"#475569", lineHeight:1.6, marginBottom:20, marginTop:4}}>
        {t("wizard.instrStep1")} <a href="#" style={{color:"#2563eb", textDecoration:"none"}} onClick={e => e.preventDefault()}>{t("wizard.learnMore")}</a>
      </p>

      {error && <div className="wiz-error">{error}</div>}

      <div className="wiz-field">
        <label className="wiz-label">{t("brand.primaryName")}</label>
        <input
          className="wiz-input"
          placeholder="z.B. Comparis"
          value={data.name}
          onChange={e => onChange("name", e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleNext()}
        />
      </div>

      <div className="wiz-field">
        <label className="wiz-label">{t("brand.websiteUrl")}</label>
        <input
          className={`wiz-input ${urlStatus === "fail" ? "error" : ""}`}
          placeholder="www.comparis.ch"
          value={data.url}
          onChange={e => handleUrlChange(e.target.value)}
        />
        {urlStatus && (
          <div className={`wiz-url-status ${urlStatus}`}>
            <span className="wiz-url-dot" />
            {urlStatus === "checking" && t("wizard.urlChecking")}
            {urlStatus === "ok"       && t("wizard.urlOk")}
            {urlStatus === "fail"     && t("wizard.urlFail")}
          </div>
        )}
      </div>

      <div className="wiz-field">
        <label className="wiz-label">{t("brand.additionalNames")}</label>
        <div className="wiz-input-row">
          <input
            className="wiz-input"
            placeholder={t("wizard.additionalNamesPlaceholder")}
            value={data.additionalInput}
            onChange={e => onChange("additionalInput", e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && data.additionalInput.trim()) {
                onChange("additionalNames", [...data.additionalNames, data.additionalInput.trim()]);
                onChange("additionalInput", "");
              }
            }}
          />
          <button className="wiz-btn wiz-btn-ghost" style={{padding:"10px 14px"}} onClick={() => {
            if (data.additionalInput.trim()) {
              onChange("additionalNames", [...data.additionalNames, data.additionalInput.trim()]);
              onChange("additionalInput", "");
            }
          }}>+</button>
        </div>
        {data.additionalNames.length > 0 && (
          <div className="wiz-tags">
            {data.additionalNames.map((n, i) => (
              <span key={i} className="wiz-tag">
                {n}
                <button className="wiz-tag-remove" onClick={() =>
                  onChange("additionalNames", data.additionalNames.filter((_, j) => j !== i))
                }>×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="wiz-field">
        <label className="wiz-label">Branche / Unternehmenstyp</label>
        <input
          className="wiz-input"
          placeholder="z.B. Vergleichsdienst, Online-Apotheke, Versicherung…"
          value={data.industry || ""}
          onChange={e => onChange("industry", e.target.value)}
        />
        <div style={{fontSize:11, color:"#94a3b8", marginTop:4}}>
          Wird automatisch erkannt beim Laden der Topics — oder hier manuell eingeben.
        </div>
      </div>

      <div style={{display:"flex", justifyContent:"flex-end", gap:10, marginTop:8}}>
        <button className="wiz-btn wiz-btn-ghost" onClick={onClose}>{t("common.cancel")}</button>
        <button className="wiz-btn wiz-btn-primary" onClick={handleNext}>{t("common.next")} →</button>
      </div>
    </>
  );
}

function Step2({ data, onChange, onNext, onBack, brandName, brandUrl }) {
  const { t } = useLanguage();
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);
  const [error, setError] = useState("");

  const fetchSuggestions = async () => {
    setLoadingAI(true);
    setSuggestions([]);
    const results = await fetchAISuggestions("topics", brandName, brandUrl);
    setSuggestions(results);
    setLoadingAI(false);
  };

  const toggleSuggestion = (s) => {
    if (data.topics.includes(s)) return; // already added
    setSelectedSuggestions(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    );
  };

  const addSelected = () => {
    const toAdd = selectedSuggestions.filter(s => !data.topics.includes(s));
    onChange("topics", [...data.topics, ...toAdd]);
    setSelectedSuggestions([]);
  };

  const addManual = () => {
    if (input.trim() && !data.topics.includes(input.trim())) {
      onChange("topics", [...data.topics, input.trim()]);
      setInput("");
    }
  };

  const removeTopic = (i) => onChange("topics", data.topics.filter((_, j) => j !== i));

  const handleNext = () => {
    if (data.topics.length === 0) { setError(t("wizard.topicErrEmpty")); return; }
    setError("");
    onNext();
  };

  return (
    <>
      <h2 className="wiz-section-title">{t("wizard.topicsTitle")}</h2>
      <p className="wiz-section-sub">Themen für die AI-Sichtbarkeitsanalyse</p>

      {error && <div className="wiz-error">{error}</div>}

      <div className="wiz-field">
        <label className="wiz-label">{t("wizard.topicInput")}</label>
        <div className="wiz-input-row">
          <input
            className="wiz-input"
            placeholder="z.B. Krankenversicherung Vergleich"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addManual()}
          />
          <button className="wiz-btn wiz-btn-ghost" style={{padding:"10px 14px"}} onClick={addManual}>+</button>
        </div>
      </div>

      <div>
        {data.topics.map((topic, i) => (
          <div key={i} className="wiz-topic-item">
            <span className="wiz-topic-bullet" />
            <span className="wiz-topic-name">{topic}</span>
            <button className="wiz-topic-del" onClick={() => removeTopic(i)}>×</button>
          </div>
        ))}
        {data.topics.length === 0 && (
          <div className="wiz-empty">
            <div className="wiz-empty-icon">📂</div>
            Noch keine Themen — füge manuell hinzu oder nutze KI-Vorschläge
          </div>
        )}
      </div>

      {/* Aside: AI suggestions panel */}
      <div style={{display:"none"}} id="aside-placeholder" />
    </>
  );

  // Note: aside is rendered by parent, passing data up via props
}

function Step3({ data, onChange, onNext, onBack, brandName, brandUrl, topics }) {
  const { t } = useLanguage();
  const [selectedTopic, setSelectedTopic] = useState(topics[0] || "");
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [loadingAI, setLoadingAI] = useState(false);
  const [error, setError] = useState("");

  const fetchSuggestions = async () => {
    if (!selectedTopic) return;
    setLoadingAI(true);
    setSuggestions([]);
    const results = await fetchAISuggestions("terms", brandName, brandUrl, [selectedTopic]);
    setSuggestions(results);
    setLoadingAI(false);
  };

  const toggleSuggestion = (s) => {
    const exists = data.terms.some(t => t.term === s && t.topic === selectedTopic);
    if (exists) return;
    setSelectedSuggestions(prev =>
      prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
    );
  };

  const addSelected = () => {
    const toAdd = selectedSuggestions
      .filter(s => !data.terms.some(t => t.term === s && t.topic === selectedTopic))
      .map(s => ({ term: s, topic: selectedTopic }));
    onChange("terms", [...data.terms, ...toAdd]);
    setSelectedSuggestions([]);
  };

  const addManual = () => {
    if (!selectedTopic) return;
    if (input.trim()) {
      onChange("terms", [...data.terms, { term: input.trim(), topic: selectedTopic }]);
      setInput("");
    }
  };

  const removeTerm = (i) => onChange("terms", data.terms.filter((_, j) => j !== i));

  const handleNext = () => {
    if (data.terms.length === 0) { setError(t("wizard.termErrEmpty")); return; }
    setError("");
    onNext();
  };

  const selectedCount = selectedSuggestions.length;
  const alreadyAdded = (s) => data.terms.some(t => t.term === s && t.topic === selectedTopic);

  return (
    <_Step3Inner
      data={data} selectedTopic={selectedTopic} setSelectedTopic={setSelectedTopic}
      input={input} setInput={setInput} suggestions={suggestions}
      selectedSuggestions={selectedSuggestions} loadingAI={loadingAI}
      error={error} topics={topics}
      addManual={addManual} removeTerm={removeTerm} handleNext={handleNext}
      fetchSuggestions={fetchSuggestions} toggleSuggestion={toggleSuggestion}
      addSelected={addSelected} selectedCount={selectedCount} alreadyAdded={alreadyAdded}
      onBack={onBack}
    />
  );
}

// ─── Main Wizard Component ────────────────────────────────────────────────────
export default function BrandSetupWizard({ userId, brandToEdit = null, onClose, onCreated }) {
  const { t, lang } = useLanguage();
  const [step, setStep] = useState(1);
  const isEditMode = brandToEdit !== null;
  const [dataLoaded, setDataLoaded] = useState(!isEditMode);

  // Shared form data - prepopulated in edit mode
  const [formData, setFormData] = useState({
    name: brandToEdit?.primary_name || "",
    url: brandToEdit?.website_url || "",
    additionalInput: "",
    additionalNames: brandToEdit?.additional_names || [],
    topics: [],
    terms: [],
    country: brandToEdit?.default_country || "Switzerland",
    language: brandToEdit?.default_language || "German",
    schedule: brandToEdit?.search_schedule || "weekly",
    industry: brandToEdit?.industry || "",
  });

  // AI suggestions state (managed here so it persists between sub-renders)
  const [topicSuggestions, setTopicSuggestions]   = useState([]);
  const [termSuggestions, setTermSuggestions]     = useState([]);
  const [selectedTopicSugg, setSelectedTopicSugg] = useState([]);
  const [selectedTermSugg, setSelectedTermSugg]   = useState([]);
  const [loadingTopicAI, setLoadingTopicAI]       = useState(false);
  const [loadingTermAI, setLoadingTermAI]         = useState(false);
  const [activeTopic, setActiveTopic]             = useState("");
  const [saving, setSaving]                       = useState(false);
  const [error, setError]                         = useState("");
  const [customTopic, setCustomTopic]             = useState("");
  const [customTopicChecked, setCustomTopicChecked] = useState(false);
  const [customTerm, setCustomTerm]               = useState("");
  const [customTermChecked, setCustomTermChecked] = useState(false);

  const update = (key, val) => setFormData(f => ({ ...f, [key]: val }));

  // ── AI fetch topics ──
  const fetchTopicSuggestions = async () => {
    setLoadingTopicAI(true);
    setTopicSuggestions([]);
    const { suggestions, industry } = await fetchAISuggestions("topics", formData.name, formData.url, [], lang, brandToEdit?.id || null, formData.industry || null);
    setTopicSuggestions(suggestions);
    // Only auto-fill industry from the server's own detection if the user
    // hasn't already typed one — otherwise this silently overwrites what
    // they entered (e.g. "pet insurance") right after they typed it.
    if (industry && !formData.industry) setFormData(prev => ({ ...prev, industry }));
    setLoadingTopicAI(false);
  };

  // ── AI fetch terms ──
  const fetchTermSuggestions = async () => {
    if (!activeTopic) return;
    setLoadingTermAI(true);
    setTermSuggestions([]);
    const { suggestions } = await fetchAISuggestions("terms", formData.name, formData.url, [activeTopic], lang);
    setTermSuggestions(suggestions);
    setLoadingTermAI(false);
  };

  // Load existing topics and terms in edit mode
  useEffect(() => {
    if (!isEditMode || dataLoaded) return;
    
    const loadExistingData = async () => {
      try {
        // Load topics
        const topicsRes = await supabase
          .from("topics")
          .select("*")
          .eq("brand_id", brandToEdit.id)
          .order("created_at");

        const loadedTopics = topicsRes.data || [];
        
        if (loadedTopics.length > 0) {
          const topicIds = loadedTopics.map(t => t.id);
          
          // Load terms
          const termsRes = await supabase
            .from("search_terms")
            .select("*")
            .in("topic_id", topicIds);

          const loadedTerms = (termsRes.data || []).map(t => {
            const topic = loadedTopics.find(tp => tp.id === t.topic_id);
            return { term: t.term, topic: topic?.name || "" };
          });

          update("topics", loadedTopics.map(t => t.name));
          update("terms", loadedTerms);
        }

        setDataLoaded(true);
      } catch (err) {
        console.error("Error loading brand data:", err);
        setDataLoaded(true);
      }
    };

    loadExistingData();
  }, [isEditMode, dataLoaded, brandToEdit]);

  useEffect(() => {
    // If no active topic, set to first
    if (formData.topics.length > 0 && !activeTopic) {
      setActiveTopic(formData.topics[0]);
    }
    // If active topic was deleted, switch to first available
    if (activeTopic && !formData.topics.includes(activeTopic)) {
      setActiveTopic(formData.topics.length > 0 ? formData.topics[0] : "");
    }
  }, [formData.topics, activeTopic]);

  // ── Auto-fetch suggestions when entering step 2 or 3 ──
  useEffect(() => {
    if (step === 2 && topicSuggestions.length === 0 && !loadingTopicAI && formData.name) {
      fetchTopicSuggestions();
    }
  }, [step]);

  useEffect(() => {
    if (step === 3 && activeTopic && !loadingTermAI) {
      setTermSuggestions([]);
      fetchTermSuggestions();
    }
  }, [step, activeTopic]);

  // ── Save to Supabase ──
  const handleSave = async () => {
    setSaving(true);
    setError("");

    try {
      let brandId;

      if (isEditMode) {
        // UPDATE existing brand
        const { error: brandErr } = await supabase
          .from("brands")
          .update({
            primary_name: formData.name,
            website_url: formData.url,
            additional_names: formData.additionalNames,
            default_country: formData.country,
            default_language: formData.language,
            search_schedule: formData.schedule,
            industry: formData.industry || null,
          })
          .eq("id", brandToEdit.id);

        if (brandErr) { setError("Fehler beim Aktualisieren."); setSaving(false); return; }
        brandId = brandToEdit.id;

        // Smart update: preserve existing topics/terms to keep ai_runs history
        const { data: existingTopics } = await supabase
          .from("topics").select("id, name").eq("brand_id", brandId);
        const existingTopicMap = {};
        (existingTopics || []).forEach(t => { existingTopicMap[t.name] = t.id; });

        const newTopicNames = formData.topics;
        const existingNames = Object.keys(existingTopicMap);

        // Delete topics no longer in form
        const toDeleteNames = existingNames.filter(n => !newTopicNames.includes(n));
        if (toDeleteNames.length > 0) {
          const idsToDelete = toDeleteNames.map(n => existingTopicMap[n]);
          await supabase.from("topics").delete().in("id", idsToDelete);
        }

        // Insert new topics
        const toInsertNames = newTopicNames.filter(n => !existingTopicMap[n]);
        if (toInsertNames.length > 0) {
          const { data: newTopics } = await supabase
            .from("topics")
            .insert(toInsertNames.map(name => ({ brand_id: brandId, name })))
            .select();
          (newTopics || []).forEach(t => { existingTopicMap[t.name] = t.id; });
        }

        // Smart-update search_terms per topic (preserve existing to keep run history)
        for (const topicName of newTopicNames) {
          const topicId = existingTopicMap[topicName];
          if (!topicId) continue;
          const newTermsForTopic = formData.terms.filter(t => t.topic === topicName).map(t => t.term);
          const { data: existingTerms } = await supabase
            .from("search_terms").select("id, term").eq("topic_id", topicId);
          const existingTermMap = {};
          (existingTerms || []).forEach(t => { existingTermMap[t.term] = t.id; });
          const termsToDelete = Object.keys(existingTermMap).filter(term => !newTermsForTopic.includes(term));
          if (termsToDelete.length > 0) {
            await supabase.from("search_terms").delete().in("id", termsToDelete.map(term => existingTermMap[term]));
          }
          const termsToInsert = newTermsForTopic.filter(term => !existingTermMap[term]);
          if (termsToInsert.length > 0) {
            await supabase.from("search_terms").insert(termsToInsert.map(term => ({ topic_id: topicId, term })));
          }
        }

        // Reload final topics for onCreated callback
        const { data: finalTopics } = await supabase.from("topics").select("*").eq("brand_id", brandId);
        setSaving(false);
        apiFetch("/hubspot/brand-event", {
          method: "POST",
          body: JSON.stringify({ event: "brand_updated", userId, brandId })
        }).catch(() => {});
        onCreated(brandId, finalTopics || [], formData.terms);
        return;

      } else {
        // CREATE new brand
        const { data: brand, error: brandErr } = await supabase
          .from("brands")
          .insert([{
            user_id: userId,
            primary_name: formData.name,
            website_url: formData.url,
            additional_names: formData.additionalNames,
            default_country: formData.country,
            default_language: formData.language,
            search_schedule: formData.schedule,
            industry: formData.industry || null,
          }])
          .select().single();

        if (brandErr) { 
          console.error("Brand creation error:", brandErr);
          const msg = brandErr.message?.includes("duplicate") || brandErr.code === "23505"
            ? "Diese URL oder Brand existiert bereits."
            : t("brand.errCreate");
          setError(msg); 
          setSaving(false); 
          return; 
        }
        brandId = brand.id;
      }

      // Insert topics (fresh for both create and edit)
      const topicRows = formData.topics.map(name => ({ brand_id: brandId, name }));
      const { data: savedTopics, error: topicsErr } = await supabase
        .from("topics").insert(topicRows).select();
      if (topicsErr) { setError("Fehler beim Speichern der Themen."); setSaving(false); return; }

      // Build topic name → id map
      const topicMap = {};
      savedTopics.forEach(tp => { topicMap[tp.name] = tp.id; });

      // Insert search terms
      if (formData.terms.length > 0) {
        const termRows = formData.terms
          .filter(t => topicMap[t.topic])
          .map(t => ({ topic_id: topicMap[t.topic], term: t.term }));
        if (termRows.length > 0) {
          const { error: termsErr } = await supabase.from("search_terms").insert(termRows);
          if (termsErr) console.warn("Terms save warning:", termsErr);
        }
      }

      setSaving(false);
      
      // Track in HubSpot (non-blocking)
      apiFetch("/hubspot/brand-event", {
        method: "POST",
        body: JSON.stringify({
          event: isEditMode ? "brand_updated" : "brand_created",
          userId,
          brandId
        })
      }).catch(err => console.warn("HubSpot tracking failed:", err));

      // Trigger page audit for new brand (non-blocking)
      if (!isEditMode && formData.url) {
        apiFetch("/brand-audit", {
          method: "POST",
          body: JSON.stringify({ brandId, url: formData.url, userId }),
        }).catch(err => console.warn("Brand audit trigger failed:", err));
      }

      // Pass brandId + saved topics (with IDs) so dashboard can open run modal directly
      onCreated(brandId, savedTopics, formData.terms);
    } catch (err) {
      console.error("Save error:", err);
      setError("Unerwarteter Fehler beim Speichern.");
      setSaving(false);
    }
  };

  const steps = [
    { num: 1, label: t("wizard.stepBrandNames") },
    { num: 2, label: t("wizard.stepTopics") },
    { num: 3, label: t("wizard.stepTerms") },
    { num: 4, label: t("wizard.stepSettings") },
  ];

  const showAside = step === 2 || step === 3;

  // ── Aside content — new checkbox design ──
  const renderAside = () => {
    if (step === 2) {
      const toggle = (s) => {
        const isAdded = formData.topics.includes(s);
        if (isAdded) {
          update("topics", formData.topics.filter(x => x !== s));
        } else {
          update("topics", [...formData.topics, s]);
        }
      };

      const handleCustomChange = (val) => {
        setCustomTopic(val);
        setCustomTopicChecked(val.trim().length > 0);
      };

      const commitCustom = () => {
        const val = customTopic.trim();
        if (val && !formData.topics.includes(val)) {
          update("topics", [...formData.topics, val]);
          setCustomTopic("");
          setCustomTopicChecked(false);
        }
      };

      return (
        <div className="wiz-aside" style={{width:"40%", minWidth:280}}>
          <div className="wiz-aside-header">
            <span className="wiz-aside-title">{t("wizard.suggestedTopics")}</span>
          </div>
          <div className="wiz-aside-list">
            {loadingTopicAI && topicSuggestions.length === 0 && (
              <div className="wiz-loading-overlay">
                <div className="wiz-loading-circle" />
                <div className="wiz-loading-text">{t("wizard.generating")}</div>
              </div>
            )}
            {topicSuggestions.map((s, i) => {
              const checked = formData.topics.includes(s);
              return (
                <label key={i} style={{display:"flex", alignItems:"flex-start", gap:10, padding:"9px 10px", borderRadius:7, cursor:"pointer", marginBottom:3, background: checked ? "#eff6ff" : "transparent", border: `1.5px solid ${checked ? "#bfdbfe" : "transparent"}`, transition:"all 0.15s"}}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(s)}
                    style={{marginTop:2, accentColor:"#2563eb", width:15, height:15, flexShrink:0}} />
                  <span style={{fontSize:13, color:"#0f172a", lineHeight:1.4}}>{s}</span>
                </label>
              );
            })}
            {!loadingTopicAI && (
              <label style={{display:"flex", alignItems:"flex-start", gap:10, padding:"9px 10px", borderRadius:7, marginBottom:3, background: customTopicChecked ? "#eff6ff" : "#f8fafc", border:`1.5px solid ${customTopicChecked ? "#bfdbfe" : "#e2e8f0"}`}}>
                <input type="checkbox" checked={customTopicChecked} readOnly
                  style={{marginTop:2, accentColor:"#2563eb", width:15, height:15, flexShrink:0}} />
                <div style={{flex:1}}>
                  <div style={{fontSize:11, color:"#94a3b8", marginBottom:4}}>{t("wizard.newTopicLabel")}</div>
                  <input className="wiz-input" style={{padding:"5px 8px", fontSize:13}}
                    placeholder={t("wizard.topicInput")} value={customTopic}
                    onChange={e => handleCustomChange(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") commitCustom(); }}
                    onBlur={commitCustom} />
                </div>
              </label>
            )}
          </div>
        </div>
      );
    }

    if (step === 3) {
      const toggle = (s) => {
        const isAdded = formData.terms.some(t => t.term === s && t.topic === activeTopic);
        if (isAdded) {
          update("terms", formData.terms.filter(t => !(t.term === s && t.topic === activeTopic)));
        } else {
          update("terms", [...formData.terms, { term: s, topic: activeTopic }]);
        }
      };

      const handleCustomChange = (val) => {
        setCustomTerm(val);
        setCustomTermChecked(val.trim().length > 0);
      };

      const commitCustom = () => {
        const val = customTerm.trim();
        if (val && activeTopic && !formData.terms.some(t => t.term === val && t.topic === activeTopic)) {
          update("terms", [...formData.terms, { term: val, topic: activeTopic }]);
          setCustomTerm("");
          setCustomTermChecked(false);
        }
      };

      return (
        <div className="wiz-aside" style={{width:"40%", minWidth:280}}>
          <div className="wiz-aside-header">
            <span className="wiz-aside-title">{t("wizard.suggestedTerms")}</span>
          </div>
          <div className="wiz-aside-list">
            {loadingTermAI && termSuggestions.length === 0 && (
              <div className="wiz-loading-overlay">
                <div className="wiz-loading-circle" />
                <div className="wiz-loading-text">{t("wizard.generating")}</div>
              </div>
            )}
            {termSuggestions.map((s, i) => {
              const checked = formData.terms.some(t => t.term === s && t.topic === activeTopic);
              return (
                <label key={i} style={{display:"flex", alignItems:"flex-start", gap:10, padding:"9px 10px", borderRadius:7, cursor:"pointer", marginBottom:3, background: checked ? "#eff6ff" : "transparent", border:`1.5px solid ${checked ? "#bfdbfe" : "transparent"}`, transition:"all 0.15s"}}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(s)}
                    style={{marginTop:2, accentColor:"#2563eb", width:15, height:15, flexShrink:0}} />
                  <span style={{fontSize:13, color:"#0f172a", lineHeight:1.4}}>{s}</span>
                </label>
              );
            })}
            {!loadingTermAI && (
              <label style={{display:"flex", alignItems:"flex-start", gap:10, padding:"9px 10px", borderRadius:7, marginBottom:3, background: customTermChecked ? "#eff6ff" : "#f8fafc", border:`1.5px solid ${customTermChecked ? "#bfdbfe" : "#e2e8f0"}`}}>
                <input type="checkbox" checked={customTermChecked} readOnly
                  style={{marginTop:2, accentColor:"#2563eb", width:15, height:15, flexShrink:0}} />
                <div style={{flex:1}}>
                  <div style={{fontSize:11, color:"#94a3b8", marginBottom:4}}>{t("wizard.newTermLabel")}</div>
                  <input className="wiz-input" style={{padding:"5px 8px", fontSize:13}}
                    placeholder={t("wizard.termInput")} value={customTerm}
                    onChange={e => handleCustomChange(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") commitCustom(); }}
                    onBlur={commitCustom} />
                </div>
              </label>
            )}
          </div>
        </div>
      );
    }
  };

  // ── Step content ──
  const renderStepContent = () => {
    if (step === 1) return (
      <Step1
        data={formData}
        onChange={update}
        onNext={() => setStep(2)}
        onClose={onClose}
        isEditMode={isEditMode}
        brandName={formData.name}
      />
    );

    if (step === 2) return (
      <>
        <h2 className="wiz-section-title">{t("wizard.topicsTitle")}</h2>
        <p className="wiz-section-sub">{t("wizard.topicsSubtitle")}</p>
        <p style={{fontSize:13, color:"#475569", lineHeight:1.6, marginBottom:16, marginTop:4}}>
          {t("wizard.instrStep2")} <a href="#" style={{color:"#2563eb", textDecoration:"none"}} onClick={e => e.preventDefault()}>{t("wizard.learnMore")}</a>
        </p>
        <p style={{fontSize:13, color:"#475569", marginBottom:20}}>
          {t("wizard.topicSelectHint") || "Wähle ein vorgeschlagenes Thema aus der Liste oder gib ein eigenes Thema ein."}
        </p>

        {/* Selected topics summary */}
        {formData.topics.length > 0 && (
          <div style={{display:"flex", flexWrap:"wrap", gap:6}}>
            {formData.topics.map((topic, i) => (
              <span key={i} className="wiz-tag" style={{fontSize:13}}>
                {topic}
                <button className="wiz-tag-remove"
                  onClick={() => update("topics", formData.topics.filter((_, j) => j !== i))}>×</button>
              </span>
            ))}
          </div>
        )}
        {formData.topics.length === 0 && (
          <div className="wiz-empty" style={{marginTop:24}}>
            <div className="wiz-empty-icon">📂</div>
            {t("wizard.topicEmptyHint")}
          </div>
        )}
      </>
    );

    if (step === 3) return (
      <>
        <h2 className="wiz-section-title">{t("wizard.termsTitle")}</h2>
        <p className="wiz-section-sub">{t("wizard.termsSubtitle")}</p>
        <p style={{fontSize:13, color:"#475569", lineHeight:1.6, marginBottom:16, marginTop:4}}>
          {t("wizard.instrStep3")} <a href="#" style={{color:"#2563eb", textDecoration:"none"}} onClick={e => e.preventDefault()}>{t("wizard.learnMore")}</a>
        </p>
        <p style={{fontSize:13, color:"#475569", marginBottom:20}}>
          {t("wizard.termSelectHint") || "Wähle zu jedem Thema mindestens eine Suchanfrage aus der Liste oder gib eine neue Suchanfrage ein."}
        </p>

        <div className="wiz-field">
          <label className="wiz-label">{t("wizard.topicLabel")}</label>
          <select className="wiz-select" value={activeTopic}
            onChange={e => {
              setActiveTopic(e.target.value);
              setTermSuggestions([]);
            }}
            style={{maxWidth:320}}
          >
            {formData.topics.map((tp, i) => (
              <option key={i} value={tp}>{tp}</option>
            ))}
          </select>
        </div>

        {/* Selected terms for active topic */}
        {formData.terms.filter(item => item.topic === activeTopic).length > 0 && (
          <div style={{display:"flex", flexWrap:"wrap", gap:6, marginTop:8}}>
            {formData.terms.filter(item => item.topic === activeTopic).map((item, i) => {
              const globalIdx = formData.terms.findIndex(t => t === item);
              return (
                <span key={i} className="wiz-tag" style={{fontSize:13}}>
                  {item.term}
                  <button className="wiz-tag-remove"
                    onClick={() => update("terms", formData.terms.filter((_, j) => j !== globalIdx))}>×</button>
                </span>
              );
            })}
          </div>
        )}
        {formData.terms.filter(item => item.topic === activeTopic).length === 0 && (
          <div className="wiz-empty" style={{marginTop:16}}>
            <div className="wiz-empty-icon">🔍</div>
            {t("wizard.termEmptyHint")}
          </div>
        )}
      </>
    );

    if (step === 4) return (
      <>
        <h2 className="wiz-section-title">{t("wizard.settingsTitle")}</h2>
        <p className="wiz-section-sub">{t("wizard.settingsSubtitle")}</p>

        {error && <div className="wiz-error">{error}</div>}

        <div className="wiz-settings-grid">
          <div className="wiz-field">
            <label className="wiz-label">{t("brand.country")}</label>
            <select className="wiz-select" value={formData.country}
              onChange={e => update("country", e.target.value)}>
              <option value="Switzerland">{t("wizard.countryCH")}</option>
              <option value="Germany">{t("wizard.countryDE")}</option>
              <option value="Austria">{t("wizard.countryAT")}</option>
              <option value="France">{t("wizard.countryFR")}</option>
              <option value="Italy">{t("wizard.countryIT")}</option>
              <option value="UK">{t("wizard.countryUK")}</option>
              <option value="USA">{t("wizard.countryUS")}</option>
            </select>
          </div>

          <div className="wiz-field">
            <label className="wiz-label">{t("brand.language")}</label>
            <select className="wiz-select" value={formData.language}
              onChange={e => update("language", e.target.value)}>
              <option value="German">{t("wizard.langDe")}</option>
              <option value="English">{t("wizard.langEn")}</option>
              <option value="French">{t("wizard.langFr")}</option>
              <option value="Italian">{t("wizard.langIt")}</option>
            </select>
          </div>
        </div>

        <div className="wiz-field">
          <label className="wiz-label">{t("brand.schedule")}</label>
          <select className="wiz-select" value={formData.schedule}
            onChange={e => update("schedule", e.target.value)}
            style={{maxWidth:300}}>
            <option value="daily">{t("brand.scheduleDaily")}</option>
            <option value="weekly">{t("brand.scheduleWeekly")}</option>
            <option value="monthly">{t("brand.scheduleMonthly")}</option>
          </select>
        </div>

        <div style={{marginTop:24, padding:16, background:"#f8fafc", borderRadius:8, border:"1px solid #e2e8f0"}}>
          <div style={{fontSize:12, fontWeight:600, color:"#64748b", marginBottom:10, textTransform:"uppercase", letterSpacing:"0.06em"}}>{t("wizard.summaryTitle")}</div>
          <div style={{fontSize:13, color:"#334155", display:"grid", gap:8}}>
            <div style={{fontWeight:600, color:"#0f172a"}}>🏷️ {formData.name} <span style={{fontWeight:400, color:"#64748b"}}>— {formData.url}</span></div>
            <div style={{display:"grid", gap:6}}>
              {formData.topics.map((topic, i) => {
                const topicTerms = formData.terms.filter(tm => tm.topic === topic);
                return (
                  <div key={i} style={{background:"white", border:"1.5px solid #e2e8f0", borderRadius:8, padding:"9px 12px"}}>
                    <div style={{display:"flex", alignItems:"center", gap:8, marginBottom: topicTerms.length ? 8 : 0}}>
                      <div style={{width:7, height:7, borderRadius:"50%", background:"#2563eb", flexShrink:0}} />
                      <span style={{fontWeight:600, color:"#0f172a", fontSize:13}}>{topic}</span>
                      <span style={{fontSize:11.5, color:"#94a3b8", marginLeft:"auto"}}>{topicTerms.length} {t("wizard.summaryTermsCount")}</span>
                    </div>
                    {topicTerms.length > 0 && (
                      <div className="wiz-tags" style={{marginTop:0, paddingLeft:15}}>
                        {topicTerms.map((tm, j) => (
                          <span key={j} className="wiz-tag" style={{fontSize:12}}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                            {tm.term}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{fontSize:12, color:"#64748b"}}>🌍 {({
                Switzerland: t("wizard.countryCH"), Germany: t("wizard.countryDE"),
                Austria: t("wizard.countryAT"), France: t("wizard.countryFR"),
                Italy: t("wizard.countryIT"), UK: t("wizard.countryUK"), USA: t("wizard.countryUS"),
              }[formData.country] || formData.country)} · {({
                German: t("wizard.langDe"), English: t("wizard.langEn"),
                French: t("wizard.langFr"), Italian: t("wizard.langIt"),
              }[formData.language] || formData.language)}</div>
          </div>
        </div>
      </>
    );
  };

  const footerInfo = step === 3
    ? `${formData.terms.length} ${t("wizard.termsWillBeCreated")}`
    : step === 2
    ? `${formData.topics.length} ${t("wizard.topicsAdded")}`
    : "";

  const canProceed = step === 2
    ? formData.topics.length > 0 || customTopic.trim().length > 0
    : step === 3
    ? formData.terms.filter(t => t.topic === activeTopic).length > 0 || customTerm.trim().length > 0
    : true;

  return (
    <div className="wiz-overlay">
      <style>{css}</style>
      <div className="wiz-shell">

        {/* ── Stepper ── */}
        <div className="wiz-header">
          <div className="wiz-steps">
            {steps.map((s, i) => (
              <React.Fragment key={s.num}>
                <div className={`wiz-step ${step === s.num ? "active" : step > s.num ? "done" : ""}`}>
                  <div className="wiz-step-num">
                    {step > s.num ? "✓" : s.num}
                  </div>
                  <div className="wiz-step-label">{s.label}</div>
                </div>
                {i < steps.length - 1 && <div className="wiz-step-divider" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="wiz-body">
          <div className="wiz-main">
            {renderStepContent()}
          </div>
          {showAside && renderAside()}
        </div>

        {/* ── Footer ── */}
        {step > 1 && (
          <div className="wiz-footer">
            <span className="wiz-footer-info">{footerInfo}</span>
            <div className="wiz-footer-actions">
              {step > 1 && (
                <button className="wiz-btn wiz-btn-ghost" onClick={() => setStep(s => s - 1)}>
                  ← {t("wizard.back")}
                </button>
              )}
              {step < 4 && (
                <button className="wiz-btn wiz-btn-primary" onClick={() => {
                  if (!canProceed) return;
                  const topicVal = customTopic.trim();
                  if (step === 2 && topicVal && !formData.topics.includes(topicVal)) {
                    update("topics", [...formData.topics, topicVal]);
                    setCustomTopic("");
                    setCustomTopicChecked(false);
                  }
                  const termVal = customTerm.trim();
                  if (step === 3 && termVal && activeTopic && !formData.terms.some(t => t.term === termVal && t.topic === activeTopic)) {
                    update("terms", [...formData.terms, { term: termVal, topic: activeTopic }]);
                    setCustomTerm("");
                    setCustomTermChecked(false);
                  }
                  setStep(s => s + 1);
                }}
                  disabled={!canProceed}
                >
                  {t("common.next")} →
                </button>
              )}
              {step === 4 && (
                <button className="wiz-btn wiz-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? <><span className="wiz-spinner" /> {t("wizard.saving")}</> : `💾 ${t("common.save")}`}
                </button>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// Dummy component to satisfy React — actual render is inlined above
function _Step3Inner() { return null; }
